import React, { useState, useEffect } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Modal
} from "react-bootstrap";
import { FaSave, FaUndo, FaTrash, FaToggleOn, FaToggleOff } from "react-icons/fa";

const AboutSectionAdmin = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/about-section");
      const data = await response.json();
      
      if (data.success) {
        setAboutData(data.data);
      } else {
        setError(data.message || "Failed to fetch about section data");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error fetching about section:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAboutData({
      ...aboutData,
      [name]: value
    });
  };

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value
    };
    setAboutData({
      ...aboutData,
      features: updatedFeatures
    });
  };

  const handlePointChange = (featureIndex, pointIndex, value) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures[featureIndex].points[pointIndex] = value;
    setAboutData({
      ...aboutData,
      features: updatedFeatures
    });
  };

  const addPoint = (featureIndex) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures[featureIndex].points.push("");
    setAboutData({
      ...aboutData,
      features: updatedFeatures
    });
  };

  const removePoint = (featureIndex, pointIndex) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures[featureIndex].points.splice(pointIndex, 1);
    setAboutData({
      ...aboutData,
      features: updatedFeatures
    });
  };

  const addFeature = () => {
    const newFeature = {
      id: Date.now(),
      icon: "flaticon-new",
      title: "New Feature",
      points: ["Point 1", "Point 2"]
    };
    setAboutData({
      ...aboutData,
      features: [...aboutData.features, newFeature]
    });
  };

  const removeFeature = (index) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures.splice(index, 1);
    setAboutData({
      ...aboutData,
      features: updatedFeatures
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/about-section", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(aboutData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("About section updated successfully!");
        setAboutData(data.data);
      } else {
        setError(data.message || "Failed to update about section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error updating about section:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/about-section/toggle", {
        method: "PATCH"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`About section ${data.data.isActive ? "activated" : "deactivated"} successfully!`);
        setAboutData(data.data);
      } else {
        setError(data.message || "Failed to toggle about section status");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error toggling about section:", err);
    }
  };

  const handleReset = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/about-section/reset", {
        method: "POST"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("About section reset to default successfully!");
        setAboutData(data.data);
        setShowResetModal(false);
      } else {
        setError(data.message || "Failed to reset about section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error resetting about section:", err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden"></span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!aboutData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Failed to load about section data</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Manage About Section</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Section Status</h5>
          <Button 
            variant={aboutData.isActive ? "success" : "secondary"}
            onClick={handleToggleActive}
          >
            {aboutData.isActive ? <FaToggleOn /> : <FaToggleOff />} 
            {aboutData.isActive ? " Active" : " Inactive"}
          </Button>
        </Card.Header>
        <Card.Body>
          <p>
            Last updated: {new Date(aboutData.updatedAt).toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Section Content</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={aboutData.title}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="subtitle"
              value={aboutData.subtitle}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Background Image URL</Form.Label>
            <Form.Control
              type="text"
              name="backgroundImage"
              value={aboutData.backgroundImage}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Features</h5>
          <Button variant="primary" onClick={addFeature}>
            Add Feature
          </Button>
        </Card.Header>
        <Card.Body>
          {aboutData.features.map((feature, index) => (
            <div key={feature.id || index} className="mb-4 border p-3 rounded">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>Feature {index + 1}</h6>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => removeFeature(index)}
                >
                  <FaTrash />
                </Button>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Icon Class</Form.Label>
                <Form.Control
                  type="text"
                  value={feature.icon}
                  onChange={(e) => handleFeatureChange(index, "icon", e.target.value)}
                  placeholder="flaticon-icon-name"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={feature.title}
                  onChange={(e) => handleFeatureChange(index, "title", e.target.value)}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label>Points</Form.Label>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => addPoint(index)}
                  >
                    Add Point
                  </Button>
                </div>
                {feature.points.map((point, pointIndex) => (
                  <div key={pointIndex} className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      value={point}
                      onChange={(e) => handlePointChange(index, pointIndex, e.target.value)}
                    />
                    <Button 
                      variant="outline-danger" 
                      className="ms-2"
                      onClick={() => removePoint(index, pointIndex)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                ))}
              </Form.Group>
            </div>
          ))}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between">
        <Button variant="secondary" onClick={() => setShowResetModal(true)}>
          <FaUndo /> Reset to Default
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="visually-hidden"></span>
            </>
          ) : (
            <><FaSave /> Save Changes</>
          )}
        </Button>
      </div>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset to Default</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset the about section to default values? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReset}>
            Reset
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AboutSectionAdmin;